import { getPathData } from "common-lib";
import { BASE_URL, client, getErrorMessage } from "./client";

const baseUrl = "/dataset";

const defaultDataSet = {
  path: "",
  name: "",
  material: "",
  type: "",
  class_list: [],
  description: "",
};

export const getDatasetFileAll = async (path: string) => {
  try {
    const response = await client.get(`${baseUrl}/dataset-file-all?path=${path}`);
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// 서버 경로에 새 폴더 생성
export const createServerDir = async (path: string, name: string) => {
  try {
    const response = await client.post(`${baseUrl}/dir`, { path, name });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// make dataset
export const addNewDataSet = async (data: DatasetFormat) => {
  try {
    const response = await client.post(`${baseUrl}`, {
      ...defaultDataSet,
      ...data,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// 검사 리뷰 데이터셋 생성: contours = { [path]: review.label_seg[] } 가 데이터셋 파일 항목의 컨투어로 들어감
export const addReviewDataSet = async (data: DatasetFormat, contours: Record<string, unknown[]>) => {
  try {
    const response = await client.post(`${baseUrl}/review`, {
      ...defaultDataSet,
      ...data,
      contours,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// 데이터셋 파일 수정 (json 파일, description 외에 모든 값 수정 가능)
export const editDataSetFileSetting = async (path: string, dataSetInfo: any) => {
  try {
    const response = await client.patch(`${baseUrl}/make-dataset-file?path=${path}`, {
      ...dataSetInfo,
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const editDatasetFile = async (id: number, classList: string[], files: Record<string, string[]>) => {
  try {
    const response = await client.patch(`${baseUrl}/file/${id}`, { classList, files });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export async function getAllFilesFromDirs(
  initialDirs: string[],
  initialFiles: string[],
  search?: { path: string; keyword: string },
  signal?: AbortSignal, // 중지 버튼 → 요청이 끊기면 백엔드 탐색도 멈춘다
) {
  const response = await client.post(
    `${baseUrl}/files`,
    { dirs: initialDirs, files: initialFiles, search },
    { signal },
  );
  return response.data as { files: string[]; failedDirs: string[]; truncated: boolean };
}

// 데이터셋 이미지(원본 파일)를 blob으로 받는다. /dataset은 Bearer 인증이라 <img src>로 직접 못 쓴다
export const getDatasetImageBlob = async (path: string): Promise<Blob> => {
  try {
    const response = await client.get(`${baseUrl}/image`, { params: { path }, responseType: "blob" });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export interface SearchMatch {
  type: "dir" | "file";
  name: string; // rootPath 기준 상대 경로 (폴더는 "sub/a", 파일은 "sub/a/img.png")
}

// 검색은 백엔드가 BFS로 처리하고 NDJSON으로 중간 결과를 흘려보낸다.
// EventSource는 Authorization 헤더를 못 실어서 fetch 스트림으로 읽는다.
export async function searchPathRecursive(
  rootPath: string,
  keyword: string,
  onPartial: (matches: SearchMatch[], scanned: number) => void,
  signal?: AbortSignal,
) {
  const params = new URLSearchParams({ path: rootPath, keyword });
  const response = await fetch(`${BASE_URL}/dataset/search?${params}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
    signal,
  });
  if (!response.ok || !response.body) throw new Error(`검색 실패 (${response.status})`);

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let rest = "";
  let result = { scanned: 0, truncated: false };

  const handle = (line: string) => {
    if (!line.trim()) return;
    const msg = JSON.parse(line);
    if (msg.type === "done") result = { scanned: msg.scanned, truncated: msg.truncated };
    else onPartial(msg.matches ?? [], msg.scanned);
  };

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    rest += decoder.decode(value, { stream: true });
    const lines = rest.split("\n");
    rest = lines.pop() ?? ""; // 잘린 마지막 줄은 다음 청크와 이어붙인다
    lines.forEach(handle);
  }
  handle(rest);
  return result;
}
